import { useMemo, useState } from 'react';

import { FEED_LABELS, fromDateTimeLocal, POOP_LEVELS, PEE_LEVELS, SIDE_LABELS, toDateTimeLocal } from '../lib/format.js';
import Modal from './Modal.jsx';

function feedInitial(subtype, profile = {}) {
  if (subtype === 'nursing') return profile.quickNursingInitial ?? 1;
  return subtype === 'breastmilk' ? (profile.quickBreastmilkInitial ?? 20) : (profile.quickFormulaInitial ?? 20);
}

function makeInitial(kind, event, profile) {
  if (event) {
    return {
      kind: event.kind,
      subtype: event.subtype ?? 'formula',
      amount: event.amount ?? (event.kind === 'feed' ? feedInitial(event.subtype ?? 'formula', profile) : 2),
      side: event.side ?? 'left',
      startedAt: toDateTimeLocal(event.startedAt),
      endedAt: event.endedAt ? toDateTimeLocal(event.endedAt) : '',
      note: event.note ?? '',
    };
  }
  return {
    kind,
    subtype: kind === 'feed' ? 'formula' : null,
    amount: kind === 'feed' ? feedInitial('formula', profile) : kind === 'poop' ? 3 : 2,
    side: 'left',
    startedAt: toDateTimeLocal(),
    endedAt: kind === 'sleep' ? toDateTimeLocal() : '',
    note: '',
  };
}

export default function EventForm({ kind = 'feed', event, profile, onClose, onSave, onDelete, busy }) {
  const initial = useMemo(() => makeInitial(kind, event, profile), [kind, event, profile]);
  const [form, setForm] = useState(initial);
  const [error, setError] = useState('');

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function submit(eventObject) {
    eventObject.preventDefault();
    setError('');
    try {
      const payload = {
        kind: form.kind,
        startedAt: fromDateTimeLocal(form.startedAt),
        endedAt: form.kind === 'sleep' && form.endedAt ? fromDateTimeLocal(form.endedAt) : null,
        note: form.note,
      };
      if (form.kind === 'feed') {
        payload.subtype = form.subtype;
        payload.amount = Number(form.amount);
        if (form.subtype === 'nursing') payload.side = form.side;
      } else if (form.kind === 'poop' || form.kind === 'pee') {
        payload.amount = Number(form.amount);
      }
      await onSave(payload);
    } catch (cause) {
      setError(cause.message);
    }
  }

  const title = event ? '编辑记录' : '手动补录';

  return (
    <Modal
      title={title}
      description="时间按中国标准时间保存"
      onClose={onClose}
      footer={(
        <>
          {event && onDelete ? (
            <button type="button" className="button button--danger-ghost" onClick={onDelete} disabled={busy}>删除</button>
          ) : <span />}
          <div className="modal-footer__actions">
            <button type="button" className="button button--secondary" onClick={onClose}>取消</button>
            <button type="submit" form="event-form" className="button button--primary" disabled={busy}>{busy ? '保存中…' : '保存'}</button>
          </div>
        </>
      )}
    >
      <form id="event-form" className="form-stack" onSubmit={submit}>
        {!event ? (
          <label className="field">
            <span>记录类型</span>
            <select value={form.kind} onChange={(e) => setForm(makeInitial(e.target.value, null, profile))}>
              <option value="feed">吃奶</option>
              <option value="poop">臭臭</option>
              <option value="pee">小便</option>
              <option value="sleep">睡眠</option>
            </select>
          </label>
        ) : null}

        {form.kind === 'feed' ? (
          <>
            <fieldset className="field-group">
              <legend>喂养方式</legend>
              <div className="segmented segmented--wrap">
                {Object.entries(FEED_LABELS).map(([value, label]) => (
                  <button key={value} type="button" className={form.subtype === value ? 'is-active' : ''} aria-pressed={form.subtype === value} onClick={() => setForm((current) => ({ ...current, subtype: value, amount: feedInitial(value, profile) }))}>{label}</button>
                ))}
              </div>
            </fieldset>
            <label className="field">
              <span>{form.subtype === 'nursing' ? '亲喂时长（分钟）' : '奶量（ml）'}</span>
              <input type="number" min={form.subtype === 'nursing' ? 1 : 5} max={form.subtype === 'nursing' ? 180 : 500} step={form.subtype === 'nursing' ? 1 : 5} value={form.amount} onChange={(e) => update('amount', e.target.value)} required />
            </label>
            {form.subtype === 'nursing' ? (
              <fieldset className="field-group">
                <legend>亲喂侧别</legend>
                <div className="segmented">
                  {Object.entries(SIDE_LABELS).map(([value, label]) => (
                    <button key={value} type="button" className={form.side === value ? 'is-active' : ''} aria-pressed={form.side === value} onClick={() => update('side', value)}>{label}</button>
                  ))}
                </div>
              </fieldset>
            ) : null}
          </>
        ) : null}

        {form.kind === 'poop' ? (
          <fieldset className="field-group">
            <legend>便量</legend>
            <div className="choice-grid choice-grid--five">
              {POOP_LEVELS.map((label, index) => (
                <button key={label} type="button" className={Number(form.amount) === index + 1 ? 'is-active' : ''} aria-pressed={Number(form.amount) === index + 1} onClick={() => update('amount', index + 1)}>{label}</button>
              ))}
            </div>
          </fieldset>
        ) : null}

        {form.kind === 'pee' ? (
          <fieldset className="field-group">
            <legend>尿量</legend>
            <div className="segmented">
              {PEE_LEVELS.map((label, index) => (
                <button key={label} type="button" className={Number(form.amount) === index + 1 ? 'is-active' : ''} aria-pressed={Number(form.amount) === index + 1} onClick={() => update('amount', index + 1)}>{label}</button>
              ))}
            </div>
          </fieldset>
        ) : null}

        <label className="field">
          <span>{form.kind === 'sleep' ? '入睡时间' : '记录时间'}</span>
          <input type="datetime-local" value={form.startedAt} onChange={(e) => update('startedAt', e.target.value)} required />
        </label>

        {form.kind === 'sleep' ? (
          <label className="field">
            <span>醒来时间</span>
            <input type="datetime-local" value={form.endedAt} onChange={(e) => update('endedAt', e.target.value)} required />
          </label>
        ) : null}

        <label className="field">
          <span>备注（可选）</span>
          <textarea rows="3" maxLength="300" value={form.note} onChange={(e) => update('note', e.target.value)} placeholder="例如：冲奶品牌、宝宝状态等" />
        </label>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
      </form>
    </Modal>
  );
}
